import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Cookies } from "react-cookie";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import ProgramApi from "../../../api/programApi";
import AdminApi from "../../../api/admin";
import TalkWorksheet from "../../../components/TalkWorksheet";
import {
  editorHtmlFromStored,
  editorSignature,
  serializeTalkWorksheet,
  speakerDisplayName,
  talkHeading,
} from "../../../utils/talkWorksheet";

const COLOR_LIST = [
  [
    "#1e3a6e",
    "#1f4e7a",
    "#0d47a1",
    "#1565c0",
    "#1976d2",
    "#0288d1",
    "#00838f",
    "#00695c",
    "#2e7d32",
  ],
  [
    "#161616",
    "#424242",
    "#616161",
    "#9e9e9e",
    "#b71c1c",
    "#c62828",
    "#e65100",
    "#ef6c00",
    "#f9a825",
  ],
  [
    "#6a1b9a",
    "#4a148c",
    "#ad1457",
    "#880e4f",
    "#4e342e",
    "#3e2723",
    "#ffffff",
    "#f5f5f5",
    "#fff8e1",
  ],
];

const TalkWorksheetEditor = ({
  show,
  talk,
  talkIndex = 0,
  retreat,
  program,
  bannerColor,
  onHide,
  onSaved,
}) => {
  const { t } = useTranslation();
  const cookies = new Cookies();
  const userData = cookies.get("LoveStrongUser") || null;
  const programApi = useMemo(() => new ProgramApi(), []);
  const adminApi = useMemo(() => new AdminApi(), []);

  const talkQuestion = talk?.talk_question;
  const talkId = talk?.id;
  const sourceKey = show ? `${talkId || ""}:${talkIndex}:${talkQuestion || ""}` : "";

  const [loadedKey, setLoadedKey] = useState("");
  const [html, setHtml] = useState("");
  const [baseline, setBaseline] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!show) {
    if (loadedKey) setLoadedKey("");
  } else if (loadedKey !== sourceKey) {
    const next = editorHtmlFromStored(talkQuestion);
    setLoadedKey(sourceKey);
    setHtml(next);
    setBaseline(next);
    setConfirmDiscard(false);
  }

  const isSpanish = retreat?.language?.toLowerCase() === "spanish";
  const heading = talk
    ? talkHeading(talk, {
        label: t("talk", { lng: isSpanish ? "spanish" : "english" }),
        index: talkIndex,
        isSpanish,
      })
    : "";

  const previewWorksheet = useMemo(
    () => ({ format: "worksheet", html }),
    [html],
  );

  const isDirty = () => editorSignature(html) !== editorSignature(baseline);

  const requestClose = () => {
    if (saving) return;
    if (isDirty()) {
      setConfirmDiscard(true);
      return;
    }
    onHide();
  };

  const handleChange = (value) => {
    setHtml(value);
  };

  const handleSave = async () => {
    if (!talk) return;
    setSaving(true);
    try {
      const text = serializeTalkWorksheet(html);
      const res = await programApi.createProgram({
        text,
        programField: "talk_question",
        retreatId: retreat && retreat.id,
        programId: program && program.id,
        roasterId: talk.id,
      });
      if (!res.success) {
        toast.error(res.msg);
        return;
      }



      toast.success(res.msg);
      if (onSaved) await onSaved();
      onHide();
    } catch (error) {
      toast.error(error?.message || String(error));
    } finally {
      setSaving(false);
    }
  };

  const editorReady = show && loadedKey === sourceKey;

  return (
    <Modal
      show={show && !!talk}
      onHide={requestClose}
      size="xl"
      centered
      className="talk-worksheet-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t("talk_questions")}
          {talk ? (
            <span className="worksheet-modal-subtitle">
              {isSpanish ? talk.spanishTalk || talk.talk : talk.talk}
              {speakerDisplayName(talk) ? ` · ${speakerDisplayName(talk)}` : ""}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="talk-worksheet-editor">
          <div className="talk-worksheet-form">
            <p className="worksheet-editor-hint">{t("worksheet_editor_hint")}</p>
            {editorReady ? (
              <SunEditor
                key={sourceKey}
                setContents={baseline}
                onChange={handleChange}
                height="460px"
                setOptions={{
                  buttonList: [
                    ["undo", "redo", "font", "fontSize", "formatBlock"],
                    ["bold", "underline", "italic", "removeFormat"],
                    [
                      "fontColor",
                      "hiliteColor",
                      "align",
                      "list",
                      "lineHeight",
                      "horizontalRule",
                    ],
                    ["link", "fullScreen"],
                  ],
                  colorList: COLOR_LIST,
                  defaultStyle:
                    'font-family: Georgia, "Palatino Linotype", "Times New Roman", serif; font-size: 16px; line-height: 1.5; color: #161616;',
                }}
              />
            ) : null}
          </div>

          <div className="talk-worksheet-preview-col">
            <div className="worksheet-preview-label">{t("worksheet_preview")}</div>
            <div className="talk-worksheet-live">
              <TalkWorksheet
                title={heading}
                speakerName={speakerDisplayName(talk)}
                bannerColor={bannerColor}
                worksheet={previewWorksheet}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="worksheet-footer">
        {confirmDiscard ? (
          <div className="worksheet-discard">
            <span>{t("unsaved_worksheet")}</span>
            <button type="button" onClick={() => setConfirmDiscard(false)}>
              {t("keep_editing")}
            </button>
            <button type="button" className="is-danger" onClick={onHide}>
              {t("discard")}
            </button>
          </div>
        ) : (
          <span />
        )}
        <div className="worksheet-footer-actions">
          <Button variant="light" onClick={requestClose} disabled={saving}>
            {t("close")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {t("save")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TalkWorksheetEditor;
